import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Button } from '@gradebook/ui';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { holdsAnyRole } from '@/lib/roles';
import { useHomeroomScopeStore } from '@/store/homeroomScopeStore';
import { useHomeroomScope } from '@/hooks/useAttendance';
import { useGradeLevelFormat } from '@/hooks/useGradeLevels';

/**
 * The homeroom in scope, as a pill on the shell's topbar.
 *
 * The register's counterpart to `CourseScopePill`, and for the same reason:
 * which group you are taking attendance for is session context — asked once
 * rather than by every attendance page — so it belongs beside the year, and the
 * page gets its width back.
 *
 * Renders only while a homeroom-scoped page is on screen
 * (`useHomeroomScopePills`), so it and the Course pill are never up together:
 * a Homeroom pill above the gradebook would be naming a scope that page does
 * not have.
 */
export function HomeroomScopePill() {
  const effectiveRoles = useAuthStore((state) => state.getEffectiveRoles());
  const scopeCount = useHomeroomScopeStore((state) => state.scopeCount);

  // `/api/homerooms` is teacher-or-admin only, so for a student or parent this
  // could only ever render a 403. The check sits above the querying component,
  // so the request is never made.
  // The whole set, not the primary office - see `AppSidebar`.
  const isStaff = holdsAnyRole(effectiveRoles, ['admin', 'teacher']);
  return isStaff && scopeCount > 0 ? <HomeroomPill /> : null;
}

function HomeroomPill() {
  const { homerooms, homeroom, selectHomeroom } = useHomeroomScope();
  const { formatSpan } = useGradeLevelFormat();

  // Nothing to switch between is not a control — a school with one homeroom
  // gets the name in the page header and no pill it can do anything with.
  if (!homeroom || homerooms.length < 2) return null;

  return (
    <Popover className="relative min-w-0">
      <PopoverButton
        aria-label="Homeroom"
        className={cn(
          'flex min-w-0 max-w-full cursor-pointer items-center gap-1.5 overflow-hidden rounded-full border border-primary-200 bg-card px-2.5 py-[7px] text-primary-900 shadow-2xs sm:gap-2 sm:px-3',
          'transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        {/* The eyebrow and the teacher's name are the pill's widest, least
            load-bearing parts; on a phone they drop and the group's name — the
            thing the control is for — stays. `aria-label` still says what it is. */}
        <span className="hidden text-[10px] font-semibold uppercase leading-none tracking-[0.06em] text-primary-400 sm:inline">
          Homeroom
        </span>
        <span className="truncate text-[13px] font-bold">{homeroom.name}</span>
        <span className="hidden truncate text-[11px] text-primary-400 lg:inline">
          {describe(homeroom.gradeLevels, homeroom.teacherName, formatSpan)}
        </span>
        <ChevronDownIcon className="size-3.5 flex-none text-primary-600" />
      </PopoverButton>

      <PopoverPanel className="absolute left-0 z-30 mt-1 max-h-[70vh] w-72 overflow-y-auto rounded-[16px] border bg-card p-1 shadow-[0_16px_40px_rgba(0,0,0,0.16)] focus:outline-none">
        {({ close }) => (
          <>
            {homerooms.map((candidate) => (
              <Button
                key={candidate.id}
                variant="ghost"
                aria-current={candidate.id === homeroom.id ? 'true' : undefined}
                onClick={() => {
                  selectHomeroom(candidate.id);
                  close();
                }}
                className={cn(
                  'h-auto w-full justify-start rounded-[10px] px-3 py-2 text-left font-normal',
                  candidate.id === homeroom.id && 'bg-primary-50'
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-neutral-900">
                    {candidate.name}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {describe(candidate.gradeLevels, candidate.teacherName, formatSpan)}
                  </span>
                </span>
                <span className="flex-none text-xs text-muted-foreground">
                  {candidate.studentCount}
                </span>
              </Button>
            ))}
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}

/**
 * "Pre-K · Mari O." — the two facts that tell one bird-named group from another.
 *
 * Either half may be missing: a homeroom is set up before a teacher is assigned,
 * and one that spans no declared grades is simply not described by grade.
 */
function describe(
  gradeLevels: number[],
  teacherName: string | null,
  formatSpan: (levels: number[] | null | undefined) => string
): string {
  const grades = gradeLevels.length > 0 ? formatSpan(gradeLevels) : null;
  return [grades, teacherName].filter(Boolean).join(' · ');
}
